import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";
import {
  Abas,
  Aviso,
  Botao,
  Campo,
  Cartao,
  Celula,
  CelulaCabecalho,
  Cabecalho,
  Chip,
  Corpo,
  Linha,
  PainelDaAba,
  Tabela,
} from "../componentes/ui";
import { PedirExportacao } from "../componentes/operacao/PedirExportacao";
import { mensagemDoConsole } from "../nucleo/console";
import {
  DIMENSOES,
  ROTULO_DA_METRICA,
  diasDoPeriodo,
  enderecoDoCsv,
  filtrosDoSalvo,
  filtrosIniciais,
  formatarDuracao,
  formatarPercentual,
  parametrosParaSalvar,
  useApagarFiltroDeRelatorio,
  useChamadosDoRelatorio,
  useFiltrosSalvos,
  useIndicadoresDeAtendimento,
  useOpcoesDoRelatorio,
  usePermanenciaDeAtendimento,
  useSalvarFiltroDeRelatorio,
  type Dimensao,
  type FiltrosDoRelatorio,
  type LinhaDeIndicadores,
  type SelecaoDeLista,
} from "../nucleo/relatorios";
import { recorteDaExportacao } from "../nucleo/operacao";
import { ROTULO_DA_URGENCIA, ROTULO_DO_ESTADO } from "../nucleo/tickets";
import { useTitulo } from "../nucleo/titulo";

type AbaDoRelatorio = "indicadores" | "estado" | "responsavel";

const ABAS = [
  { chave: "indicadores", rotulo: "Indicadores" },
  { chave: "estado", rotulo: "Permanência por estado" },
  { chave: "responsavel", rotulo: "Permanência por responsável" },
] as const;

function quando(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "America/Sao_Paulo",
      }).format(data);
}

export function Relatorios() {
  useTitulo("Relatórios de atendimento");
  const [filtros, setFiltros] = useState<FiltrosDoRelatorio>(() => filtrosIniciais());
  const [aba, setAba] = useState<AbaDoRelatorio>("indicadores");
  const [selecao, setSelecao] = useState<SelecaoDeLista | null>(null);
  const opcoes = useOpcoesDoRelatorio();
  const periodoValido = diasDoPeriodo(filtros.inicio, filtros.fim) > 0;
  const indicadores = useIndicadoresDeAtendimento(filtros, aba === "indicadores" && periodoValido);
  const permanencia = usePermanenciaDeAtendimento(
    filtros,
    aba === "responsavel" ? "responsavel" : "estado",
    aba !== "indicadores" && periodoValido,
  );

  function mudar<C extends keyof FiltrosDoRelatorio>(campo: C, valor: FiltrosDoRelatorio[C]) {
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
    setSelecao(null);
  }

  function aplicar(novos: FiltrosDoRelatorio) {
    setFiltros(novos);
    setSelecao(null);
  }

  return (
    <div data-testid="relatorios-de-atendimento">
      <h1 className="titulo-pagina">Relatórios de atendimento</h1>
      <p className="lead">
        Volume, tempos, reaberturas e cumprimento de prazo dos chamados. Clique em qualquer número
        para ver os chamados que o formam e exportar a lista para o Excel.
      </p>

      <Cartao titulo="Recorte" className="mb-5">
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="filtros-do-relatorio">
          <Campo
            rotulo="Data inicial"
            type="date"
            value={filtros.inicio}
            onChange={(evento) => mudar("inicio", evento.target.value)}
          />
          <Campo
            rotulo="Data final"
            type="date"
            value={filtros.fim}
            onChange={(evento) => mudar("fim", evento.target.value)}
          />
          <Seletor
            rotulo="Empresa"
            valor={filtros.organizacao_id}
            aoMudar={(valor) => mudar("organizacao_id", valor)}
            opcoes={(opcoes.data?.organizacoes ?? []).map((item) => [item.id, item.nome])}
          />
          <Seletor
            rotulo="Serviço"
            valor={filtros.servico}
            aoMudar={(valor) => mudar("servico", valor)}
            opcoes={(opcoes.data?.servicos ?? []).map((nome) => [nome, nome])}
          />
          <Seletor
            rotulo="Equipe"
            valor={filtros.equipe_id}
            aoMudar={(valor) => mudar("equipe_id", valor)}
            opcoes={(opcoes.data?.equipes ?? []).map((item) => [item.id, item.nome])}
          />
          <Seletor
            rotulo="Responsável"
            valor={filtros.responsavel_id}
            aoMudar={(valor) => mudar("responsavel_id", valor)}
            opcoes={(opcoes.data?.agentes ?? []).map((item) => [item.id, item.nome])}
          />
          <Seletor
            rotulo="Urgência"
            valor={filtros.urgencia}
            aoMudar={(valor) => mudar("urgencia", valor)}
            opcoes={Object.entries(ROTULO_DA_URGENCIA)}
          />
          {aba === "indicadores" ? (
            <Seletor
              rotulo="Agrupar por"
              valor={filtros.agrupar_por}
              aoMudar={(valor) => mudar("agrupar_por", valor as Dimensao | "")}
              opcoes={DIMENSOES.map(([chave, nome]) => [chave, nome])}
              vazio="Sem agrupamento"
            />
          ) : null}
        </div>
        {opcoes.isError ? <Aviso tom="erro">{mensagemDoConsole(opcoes.error)}</Aviso> : null}
        {!periodoValido ? (
          <Aviso tom="atencao">A data final não pode ser anterior à data inicial.</Aviso>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Botao variante="fantasma" onClick={() => aplicar(filtrosIniciais())}>
            Limpar filtros
          </Botao>
        </div>
        <FiltrosSalvos filtros={filtros} aba={aba} aoAplicar={aplicar} />
      </Cartao>

      <Abas
        id="relatorio"
        rotulo="Relatórios de atendimento"
        itens={ABAS}
        ativa={aba}
        aoSelecionar={(nova) => {
          setAba(nova);
          setSelecao(null);
        }}
        className="mb-4"
      />

      <PainelDaAba id="relatorio" chave="indicadores" ativa={aba} data-testid="aba-indicadores">
        {indicadores.isError ? (
          <Aviso tom="erro">{mensagemDoConsole(indicadores.error)}</Aviso>
        ) : indicadores.isPending ? (
          <p role="status">Calculando indicadores…</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span className="nota-rodape">
                Período de {indicadores.data.inicio.split("-").reverse().join("/")} a{" "}
                {indicadores.data.fim.split("-").reverse().join("/")}, dias de Brasília.
              </span>
              <a className="botao botao--fantasma" href={enderecoDoCsv("indicadores", filtros)}>
                Exportar indicadores (CSV)
              </a>
              <PedirExportacao pedido={{ tipo: "indicadores", ...recorteDaExportacao(filtros) }} />
            </div>
            <CartoesDeIndicadores linha={indicadores.data.total} aoAbrir={setSelecao} />
            {indicadores.data.linhas.length > 0 ? (
              <TabelaAgrupada
                dimensao={indicadores.data.agrupar_por}
                linhas={indicadores.data.linhas}
                aoAbrir={setSelecao}
              />
            ) : null}
          </>
        )}
      </PainelDaAba>

      {(["estado", "responsavel"] as const).map((chave) => (
        <PainelDaAba
          id="relatorio"
          chave={chave}
          ativa={aba}
          data-testid={`aba-${chave}`}
          key={chave}
        >
          {permanencia.isError ? (
            <Aviso tom="erro">{mensagemDoConsole(permanencia.error)}</Aviso>
          ) : permanencia.isPending || permanencia.data?.por !== chave ? (
            <p role="status">Calculando permanência…</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="nota-rodape">
                  {chave === "estado"
                    ? "Tempo corrido dentro do período em cada estado. Resolvido conta até o fechamento."
                    : "Tempo corrido dentro do período com cada responsável, só enquanto o chamado está aberto."}
                </span>
                <a
                  className="botao botao--fantasma"
                  href={enderecoDoCsv("permanencia", filtros, { por: chave })}
                >
                  Exportar permanência (CSV)
                </a>
                <PedirExportacao pedido={{ tipo: "permanencia", por: chave, ...recorteDaExportacao(filtros) }} />
              </div>
              <TabelaDePermanencia
                por={chave}
                linhas={permanencia.data.linhas}
                aoAbrir={setSelecao}
              />
            </>
          )}
        </PainelDaAba>
      ))}

      {selecao ? (
        <ListaDoNumero filtros={filtros} selecao={selecao} aoFechar={() => setSelecao(null)} />
      ) : null}
    </div>
  );
}

function Seletor({
  rotulo,
  valor,
  aoMudar,
  opcoes,
  vazio = "Todos",
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  opcoes: ReadonlyArray<readonly [string, string]>;
  vazio?: string;
}) {
  const id = `relatorio-${rotulo.toLocaleLowerCase("pt-BR").replace(/\s+/g, "-")}`;
  return (
    <label className="campo" htmlFor={id}>
      <span className="campo__rotulo">{rotulo}</span>
      <select
        id={id}
        className="campo__controle"
        value={valor}
        onChange={(evento) => aoMudar(evento.target.value)}
      >
        <option value="">{vazio}</option>
        {opcoes.map(([chave, nome]) => (
          <option value={chave} key={chave}>
            {nome}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------ filtros salvos */

function FiltrosSalvos({
  filtros,
  aba,
  aoAplicar,
}: {
  filtros: FiltrosDoRelatorio;
  aba: AbaDoRelatorio;
  aoAplicar: (filtros: FiltrosDoRelatorio) => void;
}) {
  const salvos = useFiltrosSalvos();
  const salvar = useSalvarFiltroDeRelatorio();
  const apagar = useApagarFiltroDeRelatorio();
  const [escolhido, setEscolhido] = useState("");
  const [nome, setNome] = useState("");
  const [movel, setMovel] = useState(false);
  const atual = salvos.data?.find((item) => item.id === escolhido);

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(
      {
        nome: nome.trim(),
        relatorio: aba === "indicadores" ? "indicadores" : "permanencia",
        parametros: parametrosParaSalvar(filtros, movel),
      },
      {
        onSuccess: (criado) => {
          setNome("");
          setEscolhido(criado.id);
        },
      },
    );
  }

  return (
    <div className="mt-4 border-t border-borda pt-3" data-testid="filtros-salvos">
      <h3 className="titulo-secao">Meus filtros</h3>
      {salvos.isError ? <Aviso tom="erro">{mensagemDoConsole(salvos.error)}</Aviso> : null}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-wrap items-end gap-2">
          <label className="campo" htmlFor="filtro-salvo">
            <span className="campo__rotulo">Filtro salvo</span>
            <select
              id="filtro-salvo"
              className="campo__controle"
              value={escolhido}
              onChange={(evento) => setEscolhido(evento.target.value)}
            >
              <option value="">
                {salvos.data?.length ? "Escolha um filtro" : "Nenhum filtro salvo"}
              </option>
              {(salvos.data ?? []).map((item) => (
                <option value={item.id} key={item.id}>
                  {item.nome}
                </option>
              ))}
            </select>
          </label>
          <Botao
            variante="fantasma"
            disabled={!atual}
            onClick={() => atual && aoAplicar(filtrosDoSalvo(atual.parametros))}
          >
            Aplicar filtro
          </Botao>
          <Botao
            variante="fantasma"
            disabled={!atual || apagar.isPending}
            onClick={() =>
              atual &&
              apagar.mutate(atual.id, {
                onSuccess: () => setEscolhido(""),
              })
            }
          >
            Apagar filtro
          </Botao>
        </div>
        <form className="flex flex-wrap items-end gap-2" onSubmit={submeter}>
          <Campo
            rotulo="Nome do filtro"
            value={nome}
            maxLength={120}
            onChange={(evento) => setNome(evento.target.value)}
          />
          <label className="flex items-center gap-2 pb-2">
            <input
              type="checkbox"
              checked={movel}
              onChange={(evento) => setMovel(evento.target.checked)}
            />
            <span>Período móvel (últimos {diasDoPeriodo(filtros.inicio, filtros.fim) || "N"} dias)</span>
          </label>
          <Botao type="submit" disabled={!nome.trim() || salvar.isPending}>
            Salvar filtro
          </Botao>
        </form>
      </div>
      {salvar.isError ? <Aviso tom="erro">{mensagemDoConsole(salvar.error)}</Aviso> : null}
      {apagar.isError ? <Aviso tom="erro">{mensagemDoConsole(apagar.error)}</Aviso> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ indicadores */

function Numero({
  rotulo,
  valor,
  metrica,
  chave,
  titulo,
  aoAbrir,
  testid,
}: {
  rotulo: string;
  valor: number;
  metrica: string;
  chave?: string;
  titulo: string;
  aoAbrir: (selecao: SelecaoDeLista) => void;
  testid?: string;
}) {
  return (
    <button
      type="button"
      className="botao botao--fantasma numero-tabular"
      aria-label={`${rotulo}: ${valor}. Ver chamados`}
      data-testid={testid}
      disabled={valor === 0}
      onClick={() => aoAbrir({ metrica, chave, titulo })}
    >
      {valor}
    </button>
  );
}

function CartoesDeIndicadores({
  linha,
  aoAbrir,
}: {
  linha: LinhaDeIndicadores;
  aoAbrir: (selecao: SelecaoDeLista) => void;
}) {
  const abrir = (metrica: string) => () => aoAbrir({ metrica, titulo: ROTULO_DA_METRICA[metrica]! });
  const cartoes: Array<{
    metrica: string;
    rotulo: string;
    principal: string;
    detalhe: string;
    quantidade: number;
  }> = [
    {
      metrica: "backlog_no_inicio",
      rotulo: "Backlog no início",
      principal: String(linha.backlog_no_inicio),
      detalhe: "Em aberto no primeiro dia",
      quantidade: linha.backlog_no_inicio,
    },
    {
      metrica: "abertos",
      rotulo: "Abertos",
      principal: String(linha.abertos),
      detalhe: "Criados no período",
      quantidade: linha.abertos,
    },
    {
      metrica: "resolvidos",
      rotulo: "Resolvidos",
      principal: String(linha.resolvidos),
      detalhe: "Resolvidos ou fechados no período",
      quantidade: linha.resolvidos,
    },
    {
      metrica: "backlog_no_fim",
      rotulo: "Backlog no fim",
      principal: String(linha.backlog_no_fim),
      detalhe: "Em aberto no último dia",
      quantidade: linha.backlog_no_fim,
    },
    {
      metrica: "primeira_resposta",
      rotulo: "Primeira resposta",
      principal: `${formatarDuracao(linha.primeira_resposta_media_segundos)} em média`,
      detalhe: `Mediana ${formatarDuracao(linha.primeira_resposta_mediana_segundos)} · ${linha.primeira_resposta_medida} medidos`,
      quantidade: linha.primeira_resposta_medida,
    },
    {
      metrica: "solucao",
      rotulo: "Solução",
      principal: `${formatarDuracao(linha.solucao_media_segundos)} em média`,
      detalhe: `Mediana ${formatarDuracao(linha.solucao_mediana_segundos)} · ${linha.solucao_medida} medidos`,
      quantidade: linha.solucao_medida,
    },
    {
      metrica: "reabertos",
      rotulo: "Taxa de reabertura",
      principal: formatarPercentual(linha.taxa_de_reabertura_percentual),
      detalhe: `${linha.reabertos} de ${linha.resolvidos} resolvidos voltaram`,
      quantidade: linha.reabertos,
    },
    {
      metrica: "resolvidos_de_primeira",
      rotulo: "Resolvidos no 1º contato",
      principal: formatarPercentual(linha.fcr_percentual),
      detalhe: `${linha.resolvidos_de_primeira} de ${linha.resolvidos} com uma única resposta da equipe`,
      quantidade: linha.resolvidos_de_primeira,
    },
  ];
  return (
    <div data-testid="indicadores-do-relatorio">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cartoes.map((cartao) => (
          <button
            type="button"
            className="rounded-xl border border-borda bg-superficie-suave p-3 text-left"
            key={cartao.metrica}
            data-testid={`cartao-${cartao.metrica}`}
            aria-label={`${cartao.rotulo}: ${cartao.principal}. ${cartao.detalhe}. Ver chamados`}
            disabled={cartao.quantidade === 0}
            onClick={abrir(cartao.metrica)}
          >
            <span className="nota-rodape">{cartao.rotulo}</span>
            <strong className="block text-[28px] numero-tabular">{cartao.principal}</strong>
            <span className="nota-rodape">{cartao.detalhe}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <CartaoDeSla
          titulo="Cumprimento do SLA de primeira resposta"
          percentual={linha.sla_primeira_resposta_percentual}
          noPrazo={linha.sla_primeira_resposta_no_prazo}
          medidos={linha.sla_primeira_resposta_medida}
          metricaNoPrazo="sla_primeira_resposta_no_prazo"
          metricaFora="sla_primeira_resposta_fora"
          aoAbrir={aoAbrir}
        />
        <CartaoDeSla
          titulo="Cumprimento do SLA de solução"
          percentual={linha.sla_solucao_percentual}
          noPrazo={linha.sla_solucao_no_prazo}
          medidos={linha.sla_solucao_medida}
          metricaNoPrazo="sla_solucao_no_prazo"
          metricaFora="sla_solucao_fora"
          aoAbrir={aoAbrir}
        />
      </div>
    </div>
  );
}

function CartaoDeSla({
  titulo,
  percentual,
  noPrazo,
  medidos,
  metricaNoPrazo,
  metricaFora,
  aoAbrir,
}: {
  titulo: string;
  percentual: number | null;
  noPrazo: number;
  medidos: number;
  metricaNoPrazo: string;
  metricaFora: string;
  aoAbrir: (selecao: SelecaoDeLista) => void;
}) {
  const fora = medidos - noPrazo;
  return (
    <div
      className="rounded-xl border border-borda bg-superficie-suave p-3"
      data-testid={`cartao-${metricaNoPrazo.replace("_no_prazo", "")}`}
    >
      <span className="nota-rodape">{titulo}</span>
      <strong className="block text-[28px] numero-tabular">{formatarPercentual(percentual)}</strong>
      <span className="nota-rodape">{medidos} medidos · mesma conta do painel de SLA</span>
      <div className="mt-2 flex flex-wrap gap-2">
        <Botao
          variante="fantasma"
          disabled={noPrazo === 0}
          onClick={() => aoAbrir({ metrica: metricaNoPrazo, titulo: ROTULO_DA_METRICA[metricaNoPrazo]! })}
        >
          {`No prazo (${noPrazo})`}
        </Botao>
        <Botao
          variante="fantasma"
          disabled={fora === 0}
          onClick={() => aoAbrir({ metrica: metricaFora, titulo: ROTULO_DA_METRICA[metricaFora]! })}
        >
          {`Fora do prazo (${fora})`}
        </Botao>
      </div>
    </div>
  );
}

function TabelaAgrupada({
  dimensao,
  linhas,
  aoAbrir,
}: {
  dimensao: Dimensao | null;
  linhas: LinhaDeIndicadores[];
  aoAbrir: (selecao: SelecaoDeLista) => void;
}) {
  const nomeDaDimensao = DIMENSOES.find(([chave]) => chave === dimensao)?.[1] ?? "Grupo";
  return (
    <div className="mt-5" data-testid="tabela-agrupada">
      <Tabela legenda={`Indicadores por ${nomeDaDimensao.toLocaleLowerCase("pt-BR")}`} legendaVisivel>
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>{nomeDaDimensao}</CelulaCabecalho>
            <CelulaCabecalho>Backlog início</CelulaCabecalho>
            <CelulaCabecalho>Abertos</CelulaCabecalho>
            <CelulaCabecalho>Resolvidos</CelulaCabecalho>
            <CelulaCabecalho>Backlog fim</CelulaCabecalho>
            <CelulaCabecalho>1ª resposta (média)</CelulaCabecalho>
            <CelulaCabecalho>Solução (média)</CelulaCabecalho>
            <CelulaCabecalho>Reabertura</CelulaCabecalho>
            <CelulaCabecalho>1º contato</CelulaCabecalho>
            <CelulaCabecalho>SLA resposta</CelulaCabecalho>
            <CelulaCabecalho>SLA solução</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {linhas.map((linha) => {
            const chave = linha.chave ?? "-";
            const numero = (rotulo: string, valor: number, metrica: string) => (
              <Numero
                rotulo={`${rotulo} de ${linha.rotulo}`}
                valor={valor}
                metrica={metrica}
                chave={chave}
                titulo={`${ROTULO_DA_METRICA[metrica]} · ${linha.rotulo}`}
                aoAbrir={aoAbrir}
              />
            );
            return (
              <Linha key={chave} data-testid={`linha-${chave}`}>
                <Celula>{linha.rotulo}</Celula>
                <Celula>{numero("Backlog no início", linha.backlog_no_inicio, "backlog_no_inicio")}</Celula>
                <Celula>{numero("Abertos", linha.abertos, "abertos")}</Celula>
                <Celula>{numero("Resolvidos", linha.resolvidos, "resolvidos")}</Celula>
                <Celula>{numero("Backlog no fim", linha.backlog_no_fim, "backlog_no_fim")}</Celula>
                <Celula>{formatarDuracao(linha.primeira_resposta_media_segundos)}</Celula>
                <Celula>{formatarDuracao(linha.solucao_media_segundos)}</Celula>
                <Celula>{formatarPercentual(linha.taxa_de_reabertura_percentual)}</Celula>
                <Celula>{formatarPercentual(linha.fcr_percentual)}</Celula>
                <Celula>{formatarPercentual(linha.sla_primeira_resposta_percentual)}</Celula>
                <Celula>{formatarPercentual(linha.sla_solucao_percentual)}</Celula>
              </Linha>
            );
          })}
        </Corpo>
      </Tabela>
    </div>
  );
}

/* ------------------------------------------------------------------ permanência */

function TabelaDePermanencia({
  por,
  linhas,
  aoAbrir,
}: {
  por: "estado" | "responsavel";
  linhas: Array<{
    chave: string;
    rotulo: string;
    chamados: number;
    total_segundos: number;
    media_segundos: number;
  }>;
  aoAbrir: (selecao: SelecaoDeLista) => void;
}) {
  const metrica = por === "estado" ? "permanencia_estado" : "permanencia_responsavel";
  if (linhas.length === 0) return <Aviso tom="marca">Nenhum chamado ativo neste recorte.</Aviso>;
  return (
    <Tabela legenda={`Permanência por ${por === "estado" ? "estado" : "responsável"}`}>
      <Cabecalho>
        <Linha>
          <CelulaCabecalho>{por === "estado" ? "Estado" : "Responsável"}</CelulaCabecalho>
          <CelulaCabecalho>Chamados</CelulaCabecalho>
          <CelulaCabecalho>Tempo total</CelulaCabecalho>
          <CelulaCabecalho>Média por chamado</CelulaCabecalho>
        </Linha>
      </Cabecalho>
      <Corpo>
        {linhas.map((linha) => (
          <Linha key={linha.chave} data-testid={`permanencia-${linha.chave}`}>
            <Celula>{linha.rotulo}</Celula>
            <Celula>
              <Numero
                rotulo={`Chamados em ${linha.rotulo}`}
                valor={linha.chamados}
                metrica={metrica}
                chave={linha.chave}
                titulo={`${ROTULO_DA_METRICA[metrica]} · ${linha.rotulo}`}
                aoAbrir={aoAbrir}
              />
            </Celula>
            <Celula>{formatarDuracao(linha.total_segundos)}</Celula>
            <Celula>{formatarDuracao(linha.media_segundos)}</Celula>
          </Linha>
        ))}
      </Corpo>
    </Tabela>
  );
}

/* ------------------------------------------------------------------ lista do número */

function ListaDoNumero({
  filtros,
  selecao,
  aoFechar,
}: {
  filtros: FiltrosDoRelatorio;
  selecao: SelecaoDeLista;
  aoFechar: () => void;
}) {
  const lista = useChamadosDoRelatorio(filtros, selecao);
  const daPermanencia = selecao.metrica.startsWith("permanencia_");
  const comValor = daPermanencia || selecao.metrica === "primeira_resposta" || selecao.metrica === "solucao";
  const recorte = daPermanencia ? { ...filtros, agrupar_por: "" as const } : filtros;
  return (
    <section className="mt-5" aria-labelledby="titulo-da-lista" data-testid="lista-do-relatorio">
      <Cartao titulo={selecao.titulo}>
        <h2 className="sr-only" id="titulo-da-lista">
          Chamados de: {selecao.titulo}
        </h2>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="nota-rodape">
            {lista.data ? `${lista.data.total} chamado(s)` : "Carregando…"}
          </span>
          <a
            className="botao botao--fantasma"
            href={enderecoDoCsv("chamados", recorte, {
              metrica: selecao.metrica,
              chave: selecao.chave,
            })}
          >
            Exportar lista (CSV)
          </a>
          <Botao variante="fantasma" onClick={aoFechar}>
            Fechar lista
          </Botao>
        </div>
        {lista.isError ? (
          <Aviso tom="erro">{mensagemDoConsole(lista.error)}</Aviso>
        ) : lista.data && lista.data.chamados.length === 0 ? (
          <Aviso tom="marca">Nenhum chamado neste número.</Aviso>
        ) : lista.data ? (
          <Tabela legenda={`Chamados: ${selecao.titulo}`}>
            <Cabecalho>
              <Linha>
                <CelulaCabecalho>Chamado</CelulaCabecalho>
                <CelulaCabecalho>Empresa</CelulaCabecalho>
                <CelulaCabecalho>Estado</CelulaCabecalho>
                <CelulaCabecalho>Aberto em</CelulaCabecalho>
                {comValor ? <CelulaCabecalho>Tempo</CelulaCabecalho> : null}
              </Linha>
            </Cabecalho>
            <Corpo>
              {lista.data.chamados.map((chamado) => (
                <Linha key={chamado.id} data-testid={`chamado-${chamado.numero}`}>
                  <Celula>
                    <Link to={`/admin/chamados/${chamado.id}`}>
                      #{chamado.numero} · {chamado.assunto}
                    </Link>
                  </Celula>
                  <Celula>{chamado.organizacao}</Celula>
                  <Celula>
                    <Chip estado="neutro">
                      {ROTULO_DO_ESTADO[chamado.estado as keyof typeof ROTULO_DO_ESTADO] ??
                        chamado.estado}
                    </Chip>
                  </Celula>
                  <Celula>{quando(chamado.criado_em)}</Celula>
                  {comValor ? <Celula>{formatarDuracao(chamado.valor_segundos)}</Celula> : null}
                </Linha>
              ))}
            </Corpo>
          </Tabela>
        ) : (
          <p role="status">Carregando chamados…</p>
        )}
      </Cartao>
    </section>
  );
}
